
import React from 'react';
import { blogPosts as posts } from '@/data/blog-posts';
import { Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/utils';

interface BlogSectionProps {
  posts: typeof posts;
}

const BlogSection = ({ posts }: BlogSectionProps) => {
  const displayedPosts = posts.slice(0, 3); // Show only the first 3 posts

  return (
    <section id="blog" className="section-padding relative">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
      </div>
      
      <div className="container mx-auto">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
            <span className="text-gradient">Latest Articles</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayedPosts.map((post, index) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="blog-card glass-morphism rounded-xl overflow-hidden hover:shadow-brand-sm transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.thumbnail} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-400 mb-3">
                    <Calendar size={14} className="mr-1" />
                    <time dateTime={post.date}>{formatDate(post.date)}</time>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-50 transition-colors duration-300">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-300 line-clamp-3">
                    {post.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          
          {/* View all link - Fixed to ensure it's clickable */}
          <div className="flex justify-center mt-10 relative z-10">
            <Link 
              to="/blog" 
              className="flex items-center px-6 py-2 rounded-full border border-gray-700 hover:border-brand text-gray-300 hover:text-white group focus-ring transition-all duration-300"
            >
              View all articles
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
